/**
 * Forced-reload scheduler for the web hard gate (V3, Workstream V).
 *
 * `resolveWebGate` (lib/versionGate.ts) decides IF a reload is required; this
 * module decides WHEN. The one hard requirement from the card: a forced reload
 * must never trap a mid-checkout user. Payment surfaces are detected centrally
 * rather than instrumented one by one:
 *
 *   1. Vendor iframes — Stripe Elements and Plaid Link both run inside iframes
 *      from their own origins. Their presence in the DOM IS the critical
 *      section, and probing for them covers every current and future mount
 *      point (paywall modal, connect flow, onboarding steps) with zero wiring.
 *   2. Protected route prefixes — multi-step flows where losing in-memory
 *      state is expensive even between payment steps.
 *   3. Manual holds — `holdForcedReload()` for any future surface the probes
 *      can't see (returns a release fn; used nowhere yet, exported on purpose).
 *
 * While unsafe, the scheduler waits (retry interval + visibilitychange) and the
 * caller keeps the dismissible UpdateBanner up, so the user is informed but
 * never interrupted. The reload-attempt marker written just before reloading is
 * what `resolveWebGate` reads back as `alreadyAttemptedFor` — one attempt per
 * target build, so no misconfiguration can loop the client.
 */

const PROTECTED_ROUTE_PREFIXES = ['/onboarding', '/welcome'];

/** Stripe Elements / Checkout and Plaid Link vendor frames. */
const VENDOR_IFRAME_SELECTOR = [
  'iframe[src*="js.stripe.com"]',
  'iframe[src*="checkout.stripe.com"]',
  'iframe[src*="cdn.plaid.com"]',
  'iframe[id^="plaid-link"]',
].join(', ');

const ATTEMPT_KEY = 'enough_forced_reload_for';
const RETRY_INTERVAL = 15_000;

let holds = 0;

/** Block the forced reload while a critical section is open. Returns release. */
export function holdForcedReload(): () => void {
  holds += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds = Math.max(0, holds - 1);
  };
}

export function isReloadSafe(
  doc: Pick<Document, 'querySelector'> = document,
  pathname: string = window.location.pathname,
): boolean {
  if (holds > 0) return false;
  if (PROTECTED_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return false;
  if (doc.querySelector(VENDOR_IFRAME_SELECTOR)) return false;
  return true;
}

/** The deployed buildHash we last forced a reload for, for resolveWebGate. */
export function getLastForcedReloadTarget(): string | null {
  try {
    return localStorage.getItem(ATTEMPT_KEY);
  } catch {
    return null;
  }
}

let pendingFor: string | null = null;
let retryTimer: ReturnType<typeof setInterval> | null = null;
let visListener: (() => void) | null = null;

function disarm(): void {
  if (retryTimer) {
    clearInterval(retryTimer);
    retryTimer = null;
  }
  if (visListener) {
    document.removeEventListener('visibilitychange', visListener);
    visListener = null;
  }
}

/** Test-only: clear scheduler + hold state. */
export function _resetForcedReloadForTests(): void {
  pendingFor = null;
  holds = 0;
  disarm();
}

/**
 * Arm a forced reload targeting `deployedBuildHash`. Reloads immediately when
 * safe; otherwise retries on an interval and on visibility changes (a hidden
 * tab that later becomes safe reloads without the user ever seeing it happen).
 * Idempotent per target.
 */
export function scheduleForcedReload(
  deployedBuildHash: string,
  reload: () => void = () => window.location.reload(),
): void {
  if (!deployedBuildHash || pendingFor === deployedBuildHash) return;
  pendingFor = deployedBuildHash;

  let fired = false;
  const attempt = () => {
    if (fired || !isReloadSafe()) return;
    fired = true;
    // Stamp BEFORE reloading: if we come back still gated, resolveWebGate sees
    // the marker and degrades to the banner instead of reloading again.
    try {
      localStorage.setItem(ATTEMPT_KEY, deployedBuildHash);
    } catch { /* no storage — the in-memory pendingFor still caps this tab */ }
    disarm();
    reload();
  };

  attempt();
  if (!fired && !retryTimer) {
    retryTimer = setInterval(attempt, RETRY_INTERVAL);
    visListener = attempt;
    document.addEventListener('visibilitychange', attempt);
  }
}
